import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import featureFlags from 'shared/lib/featureFlags';
import Icon from 'shared/components/Icon';
import Tooltip from 'shared/components/Tooltip';
import {
  TRANSLATED_TYPES,
  getTranslatedTypePath,
  NOT_SPECIFIED_SLUG,
  LANGUAGE_LEVELS,
  MATCH,
} from 'shared/constants';
import styles from './Tag.module.scss';

const levelClass = ['default', 'low', 'medium', 'high'];
const langLevelClass = ['default', 'low', 'low2', 'medium', 'medium2', 'high', 'high2'];
const modeIcons = {
  u: 'la la-chevron-circle-up',
  d: 'la la-chevron-circle-down',
  default: 'la la-pause-circle'
};
const modeTexts = {
  u: 'modeUp',
  d: 'modeDown',
  default: 'modeExact'
};

const voidCb = () => {};

// translate type prefix
const transTypes = [
  'field',
  'role',
  'skill',
  'company',
  'country',
  'city',
  'employment',
  'money',
  'benefit',
  'rate',
  'language',
  'companySize',
  'companyType'
];

const Tag = ({
  component: Component,
  slug,
  type,
  subType,
  level,
  mode,
  children,
  onClick,
  onRemove,
  onToggleRequired,
  onToggleMode,
  onAddToProfile,
  wrapperClassName,
  className,
  iconClassName,
  silentMatch,
  match,
  app,
  kind,
  isRequired,
  isApproved,
  isDisabled,
  actionDisabled,
  isSmall,
  clickToSelect,
  noPlusIcon,
  tooltip,
  useChildrenForTooltip,
  linkUrl,
  noTooltip,
  ignored,
  noIcon,
  canSelect,
  noLevelColor
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const modeIcon = mode in modeIcons ? modeIcons[mode] : modeIcons.default;

  const onTagClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (onClick) {
      onClick();
    } else if (linkUrl) {
      navigate(linkUrl);
    }
  };

  const onRemoveClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove();
  };

  const onToggleRequiredClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onToggleRequired();
  };

  const onToggleModeClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onToggleMode();
  };

  const onAddToProfileClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onAddToProfile();
  };

  const getContent = (forTooltip = false) => {
    if (forTooltip && useChildrenForTooltip) {
      return children;
    }

    if (TRANSLATED_TYPES.includes(type) && slug) {
      return t(getTranslatedTypePath(type, slug));
    }

    if (type === 'language') {
      return `${t(`language.${slug}`)}${level ? ` (${LANGUAGE_LEVELS[level]})` : ''}`;
    }

    if (children === NOT_SPECIFIED_SLUG || slug === NOT_SPECIFIED_SLUG) {
      return t('notSpecified');
    }

    return children;
  };

  const getTooltip = () => {
    if (noTooltip) {
      return null;
    }

    if (tooltip) {
      return tooltip;
    }

    if (type === 'text') {
      return null;
    }

    const typeText = transTypes.includes(type) ? `${t(`searchItem.${type}`)}: ` : '';
    const modeText = featureFlags.search && onToggleMode && level > 0 ? ` (${t(`tag.${modeTexts[mode] || modeTexts.default}`)})` : null;
    const levelRow = subType !== 'soft' && level > 0 && !isDisabled
      ? (
        <p>
          {t(`match.${kind === 'profile' || kind === 'profile-form' ? 'level' : 'requiredLevel'}`)}:
          {' '}
          {type === 'language' ? (
            <span className={`langLevel-${level}`}>
              {t(`languageLevel.${level}`)}{modeText}
              {!!modeText && (
                <i className={cn(styles.levelModeIconTooltip, modeIcon, { [styles.rotate90]: modeIcon === modeIcons.default })} />
              )}
            </span>
          ) : (
            <span className={`level-${level}`}>
              {t(`tag.level-${level}`)}{modeText}
              {!!modeText && (
                <i className={cn(styles.levelModeIconTooltip, modeIcon, { [styles.rotate90]: modeIcon === modeIcons.default })} />
              )}
            </span>
          )}
        </p>
      )
      : null;
    const isRequiredRow = isRequired !== null && (type === 'language' || type === 'skill') ? (
      <p>
        {t(`match.${type}-is${!isRequired ? 'Not' : ''}Required`)}
        {featureFlags.search && onToggleRequired && (
          <i className={cn('la la-exclamation-circle', styles.requiredIconToolip, { [styles.requiredIconDisabled]: !isRequired })} />
        )}
      </p>
    ) : null;
    const matchingText = match !== null && !ignored ? t(`${app}:match.${type}-${match}`) : null;

    const addToProfileText = match === 'none' && app === 'userportal' && onAddToProfile !== null && !ignored
      ? (
        <p className={styles.addToProfileHint}>
          <i className={cn('la la-user-plus', styles.addToProfileHintIcon)} />
          {t(`${app}:match.${type}-addToProfile`)}
        </p>
      )
      : null;
    const ignoredText = ignored ? <p className={styles.ignoredText}>{t('tag.ignoredSkill')}</p> : null;
    const formHelp = (kind === 'profile-form' || kind === 'job-form' || kind === 'search') && onClick !== null && !isDisabled ? (<p>{t('tag.toggleLevel')}</p>) : null;
    const clickToSelectText = clickToSelect ? (<p>{t('tag.clickToSelect')}</p>) : null;

    const content = getContent(true);
    const header = content && content.includes(typeText) ? content : `${typeText}${content}`;
    return (
      <>
        <p>
          {levelRow ? <strong>{header}</strong> : <>{header}</>}
        </p>
        {levelRow}
        {isRequiredRow}
        {matchingText && <p>{matchingText}</p>}
        {ignoredText}
        {addToProfileText}
        {clickToSelectText || formHelp}
        {!isApproved && <p><i className={cn('la la-warning', styles.notApprovedWarning)} /><i>{t(`tag.${type}-isNotApproved`)}</i></p>}
      </>
    );
  };

  let clickHandler;
  if (onClick !== null) {
    clickHandler = onTagClick;
  } else if (match === 'none' && onAddToProfile !== null && !ignored) {
    clickHandler = onAddToProfile;
  }

  const colorClassSource = type === 'language' ? langLevelClass : levelClass;
  const colorClass = !noLevelColor
    ? type === 'skill' && subType === 'soft'
      ? styles.softSkill
      : styles[colorClassSource[parseInt(level, 10)] || 'default']
    : styles.default;
  const iconType = type === 'skill' && subType === 'soft' ? 'soft-skill' : type;

  const tag = (
    <>
      <span
        onClick={clickHandler || voidCb}
        className={cn(
          styles.tag, styles[type], colorClass,
          {
            [styles.role]: type === 'role',
            [styles.money]: type === 'money',
            [styles.clickable]: !!clickHandler,
            [styles.isRequired]: isRequired === true,
            [styles.notApproved]: isApproved !== true,
            [styles.isDisabled]: isDisabled === true,
            [styles.isSmall]: isSmall === true,
            [styles.canSelect]: !!canSelect,
          },
          className
        )}
      >
        {!noIcon && (
          <span className={styles.icon}>
            {ignored ? (
              <Icon type="default" />
            ) : (
              <Icon type={iconType} className={iconClassName} slug={slug} />
            )}
          </span>
        )}
        {linkUrl ? (
          <a href={linkUrl} onClick={onTagClick} className={styles.title}>{getContent()}</a>
        ) : (
          <span className={styles.title}>
            {getContent()}
          </span>
        )}
        {featureFlags.search && onToggleMode && level > 0 && (
          <span className={styles.action}>
            <a href="" onClick={onToggleModeClick}>
              <i className={cn(modeIcon, styles.levelMode, { [styles.rotate90]: modeIcon === modeIcons.default })} />
            </a>
          </span>
        )}
        {featureFlags.search && onToggleRequired && (
          <span className={styles.action}>
            <a href="" onClick={onToggleRequiredClick}>
              <i className={cn('la la-exclamation-circle', styles.required, { [styles.selected]: isRequired })} />
            </a>
          </span>
        )}
        {clickToSelect && !noPlusIcon && (
          <span className={styles.action}>
            <i className={cn('la la-plus-circle', styles.clickToSelect)} />
          </span>
        )}
        {onRemove !== null && (
          <span className={cn(styles.action, { [styles.actionDisabled]: actionDisabled })}>
            {actionDisabled ? (
              <i className={`la la-times-circle-o ${styles.remove}`} />
            ) : (
              <a href="" onClick={onRemoveClick}><i className={`la la-times-circle-o ${styles.remove}`} /></a>
            )}
          </span>
        )}
      </span>
      {match !== null && !ignored && (
        <>
          {!silentMatch && (
            <span className={cn(
              styles.match,
              {
                [styles.partialMatch]: match === 'partial',
                [styles.fullMatch]: match === 'full',
                [styles.noMatch]: match === 'low' || match === 'none',
              }
            )}
            >
              <i className={`la la-thumbs-${match === 'low' || match === 'none' ? 'down' : 'up'}`} />
            </span>
          )}
          {match === 'none' && onAddToProfile !== null && (
            <a href="" onClick={onAddToProfileClick} className={styles.addToProfile}><i className="la la-user-plus" /></a>
          )}
        </>
      )}
      {ignored && (
        <span className={cn(styles.ignored, { [styles.isSmall]: isSmall === true })}>
          <i className="la la-ban" />
        </span>
      )}
    </>
  );

  const tooltipContent = getTooltip();
  return (
    <Component className={cn(styles.wrapper, { [styles.withMatch]: match !== null }, wrapperClassName)}>
      {tooltipContent !== null ? <Tooltip content={tooltipContent}>{tag}</Tooltip> : tag}
    </Component>
  );
};

Tag.propTypes = {
  component: PropTypes.oneOf(['span', 'li']),
  slug: PropTypes.string,
  type: PropTypes.oneOf([
    'field', 'role', 'skill', 'company', 'country', 'city', 'employment', 'money',
    'benefit', 'rate', 'text', 'language', 'companySize', 'companyType', 'homeoffice',
    'remoteWork', 'suitableFor', 'default'
  ]),
  subType: PropTypes.string,
  level: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mode: PropTypes.oneOf(['u', 'd']),
  onClick: PropTypes.func,
  onRemove: PropTypes.func,
  onToggleRequired: PropTypes.func,
  onToggleMode: PropTypes.func,
  onAddToProfile: PropTypes.func,
  wrapperClassName: PropTypes.string,
  className: PropTypes.string,
  iconClassName: PropTypes.string,
  match: PropTypes.oneOf(_.values(MATCH)),
  app: PropTypes.oneOf(['backoffice', 'userportal']),
  kind: PropTypes.oneOf(['job', 'profile', 'profile-form', 'job-form', 'search']),
  isRequired: PropTypes.bool,
  isApproved: PropTypes.bool,
  isDisabled: PropTypes.bool,
  actionDisabled: PropTypes.bool,
  isSmall: PropTypes.bool,
  clickToSelect: PropTypes.bool,
  noPlusIcon: PropTypes.bool,
  tooltip: PropTypes.node,
  useChildrenForTooltip: PropTypes.bool,
  linkUrl: PropTypes.string,
  noTooltip: PropTypes.bool,
  ignored: PropTypes.bool,
  noIcon: PropTypes.bool,
  canSelect: PropTypes.bool,
  silentMatch: PropTypes.bool,
  noLevelColor: PropTypes.bool,
};

Tag.defaultProps = {
  component: 'span',
  slug: null,
  type: 'default',
  subType: null,
  level: 0,
  mode: null,
  onClick: null,
  onRemove: null,
  onToggleRequired: null,
  onToggleMode: null,
  onAddToProfile: null,
  className: '',
  wrapperClassName: '',
  iconClassName: null,
  match: null,
  app: 'userportal',
  kind: 'job',
  isRequired: null,
  isApproved: true,
  isDisabled: false,
  actionDisabled: false,
  isSmall: false,
  clickToSelect: false,
  noPlusIcon: false,
  tooltip: null,
  useChildrenForTooltip: false,
  linkUrl: null,
  noTooltip: false,
  ignored: false,
  noIcon: false,
  canSelect: false,
  silentMatch: false,
  noLevelColor: false,
};

export default Tag;
